import React, { useContext, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { Load } from '../components/widgets/spinner/Spinner';
import { Context } from '../components/authentication/AuthContext';
import { MenuProvider, useMenu } from '../components/contexts/MenuContext';

// Importação das Rotas Privadas
const PrivateRoutes = lazy(() => import('./PrivateRoutes'));

// Importações Lazy das páginas públicas
const Empresa = lazy(() => import('../public/establishment/Empresa'));
const EmpresaSecoes = lazy(() => import('../public/establishment/EmpresaSecoes'));
const SecoesDetalhes = lazy(
  () => import('../public/establishment/orders/orders-secoes/SecoesDetalhes')
);
const OrdersList = lazy(() => import('../public/establishment/orders/orders-list/OrdersList'));
const OrderDetail = lazy(() => import('../public/establishment/orders/order-detail/OrderDetail'));
const Cart = lazy(() => import('../components/partials/modals/Cart'));
const OrderDetails = lazy(() => import('../public/establishment/orders/order-details/OrderDetails'));
const CloserOrder = lazy(() => import('../public/establishment/orders/order-close/CloseOrder'));
const Login = lazy(() => import('../public/Login/Login'));
const LoginApp = lazy(() => import('../public/LoginApp/LoginApp'));
const LoginAppPdv = lazy(() => import('../public/LoginAppPdv/LoginAppPdv'));
const SelectProductModal = lazy(
  () => import('../components/partials/modals/SelectProductModal.jsx')
);
const SearchUrl = lazy(() => import('../private/Search-Url/SearchUrl'));
const MesaCliente = lazy(() => import('../private/MesaQRCode/MesaCliente.jsx'));
const MesaClienteLista = lazy(() => import('../private/MesaQRCode/MesaClienteLista.jsx'));
const TesteGratuito = lazy(() => import('../public/TesteGratuito/TesteGratuito'));
const LoginSemOrg = lazy(() => import('../public/LoginSemOrg/LoginSemOrg'));
const PrivacidadeBalanca = lazy(() => import('../public/balanca/Privacidade/PrivacidadeBalanca'));

const EmpresaVarejo = lazy(() => import('../public/establishment/EmpresaVarejo'));
const EmpresaVarejoSecoes = lazy(() => import('../public/establishment/EmpresaVarejoSecoes'));
const SecoesVarejoDetalhes = lazy(
  () => import('../public/establishment/orders/orders-secoes/SecoesVarejoDetalhes')
);
const CampanhaDetalhes = lazy(() => import('../public/establishment/campanhas/CampanhaDetalhes'));
const CampanhaVarejoDetalhes = lazy(
  () => import('../public/establishment/campanhas/CampanhaVarejoDetalhes')
);
const OrdersListVarejo = lazy(
  () => import('../public/establishment/orders/orders-list/OrdersListVarejo')
);
const OrderDetailVarejo = lazy(
  () => import('../public/establishment/orders/order-detail/OrderDetailVarejo')
);
const CartVarejo = lazy(() => import('../components/partials/modals/CartVarejo'));
const CloserOrderVarejo = lazy(
  () => import('../public/establishment/orders/order-close/CloseOrderVarejo')
);
const SelectProductVarejoModal = lazy(
  () => import('../components/partials/modals/SelectProductVarejoModal.jsx')
);
const EnderecoEntrega = lazy(
  () => import('../components/partials/pages/EnderecoEntrega/EnderecoEntrega.jsx')
);

const EmpresaRouter = () => {
  const { empresa } = useParams();
  const [searchParams] = useSearchParams();
  const { orgData, fetchMenu, loading } = useMenu();

  useEffect(() => {
    fetchMenu(empresa);
  }, [empresa, fetchMenu]);

  // Cupom vindo por QR code/URL na entrada do cardápio: guarda na sessão
  // para sobreviver à navegação até a tela de fechamento do pedido.
  useEffect(() => {
    const cupom = searchParams.get('cupom')?.trim().toUpperCase();
    if (cupom && empresa) {
      sessionStorage.setItem(`cupom_url_${empresa}`, cupom);
    }
  }, [searchParams, empresa]);

  if (loading && !orgData) return <Load />;

  const modelo = orgData?.modelo_cardapio || 'lista';

  return modelo === 'secoes' ? <EmpresaSecoes /> : <Empresa />;
};

const VarejoRouter = () => {
  const { empresa } = useParams();
  const [searchParams] = useSearchParams();
  const { orgData, fetchMenu, loading } = useMenu();

  useEffect(() => {
    fetchMenu(empresa);
  }, [empresa, fetchMenu]);

  // Cupom vindo por QR code/URL na entrada da loja: guarda na sessão
  // para sobreviver à navegação até a tela de fechamento do pedido.
  useEffect(() => {
    const cupom = searchParams.get('cupom')?.trim().toUpperCase();
    if (cupom && empresa) {
      sessionStorage.setItem(`cupom_url_${empresa}`, cupom);
    }
  }, [searchParams, empresa]);

  if (loading && !orgData) return <Load />;

  const modelo = orgData?.modelo_cardapio || 'lista';

  return modelo === 'secoes' ? <EmpresaVarejoSecoes /> : <EmpresaVarejo />;
};

const MesaRouter = () => {
  const { empresa } = useParams();
  const { orgData, fetchMenu, loading } = useMenu();

  useEffect(() => {
    if (empresa) fetchMenu(empresa);
  }, [empresa, fetchMenu]);

  if (loading && !orgData) return <Load />;

  // Se o modelo for 'secoes', carrega o MesaCliente (estilo abas)
  // Caso contrário (ou se for 'lista'), carrega o MesaClienteLista
  const modelo = orgData?.modelo_cardapio || 'lista';

  return modelo === 'secoes' ? <MesaCliente /> : <MesaClienteLista />;
};

const Rout = () => {
  const { isAuthenticated, searchingUrl } = useContext(Context);
  const location = useLocation();

  // Define se a rota atual pertence ao ecossistema de cardápio/loja pública
  const isPublicRoute =
    location.pathname.startsWith('/menu') ||
    location.pathname.startsWith('/loja') ||
    location.pathname.startsWith('/mesa') ||
    location.pathname.includes('/auth/login') ||
    location.pathname === '/teste-gratuito' ||
    location.pathname === '/login' ||
    location.pathname.startsWith('/balanca');

  return (
    <MenuProvider>
      <Suspense fallback={<Load />}>
        {isPublicRoute ? (
          /* --- SITE (Público / Cliente) --- */
          <Routes>
            <Route path="/:cliente/auth/login" element={<Login />} />
            <Route path="/:cliente/app/auth/login" element={<LoginApp />} />
            <Route path="/:cliente/app/pdv/auth/login" element={<LoginAppPdv />} />
            <Route path="/teste-gratuito" element={<TesteGratuito />} />
            <Route path="/login" element={<LoginSemOrg />} />

            {/* App Android da balança: política exigida pela Google Play */}
            <Route path="/balanca/privacidade" element={<PrivacidadeBalanca />} />

            {/* MESA QR CODE */}
            <Route path="/mesa/:empresa/:guid" element={<MesaRouter />} />

            {/* Rotas de Menu */}
            <Route path="/menu/:empresa" element={<EmpresaRouter />} />
            <Route path="/menu/:empresa/secoes/:cod_secao" element={<SecoesDetalhes />} />
            <Route path="/menu/:empresa/campanhas/:cod_promocoes" element={<CampanhaDetalhes />} />
            <Route path="/menu/:empresa/pedidos" element={<OrdersList />} />
            <Route path="/menu/:empresa/pedido/:id" element={<OrderDetail />} />
            <Route path="/menu/:empresa/carrinho" element={<Cart />} />
            <Route path="/menu/:empresa/detalhes" element={<OrderDetails />} />
            <Route path="/menu/:empresa/endereco" element={<EnderecoEntrega tipo="menu" />} />
            <Route path="/menu/:empresa/fechar-pedido" element={<CloserOrder />} />
            <Route path="/menu/:empresa/adicionar-pedido" element={<SelectProductModal />} />

            {/* Rotas de Loja / Varejo */}
            <Route path="/loja/:empresa" element={<VarejoRouter />} />
            <Route path="/loja/:empresa/secoes/:cod_secao" element={<SecoesVarejoDetalhes />} />
            <Route path="/loja/:empresa/campanhas/:cod_promocoes" element={<CampanhaVarejoDetalhes />} />
            <Route path="/loja/:empresa/pedidos" element={<OrdersListVarejo />} />
            <Route path="/loja/:empresa/pedido/:id" element={<OrderDetailVarejo />} />
            <Route path="/loja/:empresa/carrinho" element={<CartVarejo />} />
            <Route path="/loja/:empresa/endereco" element={<EnderecoEntrega tipo="loja" />} />
            <Route path="/loja/:empresa/fechar-pedido" element={<CloserOrderVarejo />} />
            <Route path="/loja/:empresa/adicionar-pedido" element={<SelectProductVarejoModal />} />
          </Routes>
        ) : isAuthenticated ? (
          /* --- ADMIN (Privado / Logado) --- */
          <PrivateRoutes />
        ) : (
          /* --- FALLBACK (Login ou Busca de URL) --- */
          <Routes>
            {searchingUrl && !isAuthenticated && !sessionStorage.getItem('cod_org') ? (
              <Route path="/:url?" element={<SearchUrl />} />
            ) : (
              <Route path="*" element={<Login />} />
            )}
          </Routes>
        )}
      </Suspense>
    </MenuProvider>
  );
};

export default Rout;
